/**
 * HourlyChart — Average Alarms and Response Times by Hour (bar + line dual axis).
 * Bar colors are per-hour based on shift assignment from the API.
 * Includes shift legend and response time average annotation line.
 *
 * Grandstream campuses get a dashed "Average Time to Connect" line on the same right-hand
 * minutes axis (docs/TIME_TO_CONNECT_PLAN.md, phase 3), from chartData.connects. An hour
 * with no intercom alert is null and leaves a gap: N/A, never a 0-minute point.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { hourToLocalizedHour } from '../../../../libs.jsx';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';
import { useChartSeries } from '../../../../components/charts/chartPalette.js';

Chart.register(...registerables);
Chart.register(annotationPlugin);

const HOURLY_CHART_TOOLTIP = [
    'This chart shows the average number of alerts per hour of the day (bars) and the average caregiver response time, in minutes, for those alerts (line) over the last 28 days.',
    'Bars are color coordinated by shift so you can quickly see how alert volume and response times shift between Day, Evening, and Night.',
    'The dashed horizontal line marks the overall average response time across all hours.',
];

const CONNECT_TOOLTIP = 'The dashed green line is the average Time to Connect for each hour: the time until the '
    + 'first intercom call to the resident or their room was answered, over alerts with an answered intercom call. '
    + 'Hours with none have no point.';

/* Green from the theme palette (#09BA00 in both): clear of the orange response line and
   the blue/indigo shift bars. */
const CONNECT_SERIES_INDEX = 11;

/** Sort shift hours chronologically, keeping wrap-around shifts (e.g. 9pm–6am) in shift order rather than 0-23 order. */
const orderShiftHours = (hours) => {
    if (!hours || hours.length === 0) return [];
    const sorted = [...new Set(hours)].sort((a, b) => a - b);

    const segments = [];
    let segStart = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === prev + 1) {
            prev = sorted[i];
        } else {
            segments.push([segStart, prev]);
            segStart = sorted[i];
            prev = sorted[i];
        }
    }
    segments.push([segStart, prev]);

    // Wrap-around: e.g. [0..6] and [21..23] → render late segment first
    if (segments.length === 2 && segments[0][0] === 0 && segments[1][1] === 23) {
        const lateSegment = sorted.filter((h) => h >= segments[1][0]);
        const earlySegment = sorted.filter((h) => h <= segments[0][1]);
        return [...lateSegment, ...earlySegment];
    }

    return sorted;
};

/** Shift legend item with hover popover showing hours */
const ShiftLegendItem = ({ name, color, hours }) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleEnter = () => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setStyle({
                position: 'fixed',
                top: `${rect.bottom + 6}px`,
                left: `${rect.left}px`,
                zIndex: 9999,
            });
        }
        setOpen(true);
    };

    return (
        <div ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', cursor: 'default', position: 'relative' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: color, display: 'inline-block', flexShrink: 0 }} />
            {name}
            {open && hours.length > 0 ? (
                <div style={{
                    ...style,
                    background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
                    padding: 'var(--space-3) var(--space-4)', minWidth: '140px',
                }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: color, display: 'inline-block' }} />
                        {name} Shift
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}>
                        {hours.map((h) => (
                            <span key={h}>{hourToLocalizedHour(h)}</span>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const HourlyChart = ({ chartData, avgResponseTime, isGrandstream = false }) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const series = useChartSeries();
    const connectColor = series[CONNECT_SERIES_INDEX];

    // Build per-bar colors from shift data
    const barColors = useMemo(() => {
        const hours = chartData?.hours || [];
        const hourShift = chartData?.hourShift || {};
        const shiftColor = chartData?.shiftColor || {};
        return hours.map((h) => shiftColor[hourShift[h.toString()]] || '#0081C7');
    }, [chartData]);

    // Build labels with localized hour
    const labels = useMemo(() => {
        const hours = chartData?.hours || [];
        return hours.map((h) => hourToLocalizedHour(h));
    }, [chartData]);

    // Build legend items from unique shifts + group hours per shift
    const shiftLegend = useMemo(() => {
        const shiftColor = chartData?.shiftColor || {};
        const hourShift = chartData?.hourShift || {};
        const hours = chartData?.hours || [];

        // Group hours by shift name
        const shiftHoursMap = {};
        hours.forEach((h) => {
            const shiftName = hourShift[h.toString()];
            if (shiftName) {
                if (!shiftHoursMap[shiftName]) shiftHoursMap[shiftName] = [];
                shiftHoursMap[shiftName].push(h);
            }
        });

        return Object.entries(shiftColor).map(([name, color]) => ({
            name,
            color,
            hours: orderShiftHours(shiftHoursMap[name] || []),
        }));
    }, [chartData]);

    useEffect(() => {
        if (!canvasRef.current) return;

        const barData = chartData?.avg_events || [];
        const lineData = chartData?.responses || [];

        if (chartRef.current) {
            chartRef.current.data.labels = labels;
            chartRef.current.data.datasets[0].data = barData;
            chartRef.current.data.datasets[0].backgroundColor = barColors;
            chartRef.current.data.datasets[1].data = lineData;
            chartRef.current.options.plugins.annotation.annotations.line1.value = avgResponseTime || 0;
            chartRef.current.options.plugins.annotation.annotations.line1.label.content = `Response Time Average: ${avgResponseTime || 0}`;
            chartRef.current.update();
        } else {
            chartRef.current = new Chart(canvasRef.current, {
                data: {
                    labels,
                    datasets: [
                        {
                            type: 'bar',
                            label: 'Average Alarms/day',
                            data: barData,
                            backgroundColor: barColors,
                            hoverBackgroundColor: '#0081C778',
                            yAxisID: 'a',
                            borderRadius: 5,
                            order: 2,
                        },
                        {
                            type: 'line',
                            label: 'Average Response Time',
                            data: lineData,
                            backgroundColor: '#BE4D25',
                            hoverBackgroundColor: '#BE4D2578',
                            borderColor: '#BE4D25',
                            borderWidth: 3,
                            pointRadius: 5,
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 1,
                            pointHoverRadius: 9,
                            pointBackgroundColor: '#BE4D25',
                            pointHoverBackgroundColor: '#BE4D25',
                            pointHoverBorderColor: '#ffffff',
                            pointHoverBorderWidth: 2,
                            tension: 0.4,
                            fill: false,
                            yAxisID: 'b',
                            order: 1,
                        },
                        ...(isGrandstream ? [{
                            type: 'line',
                            label: 'Average Time to Connect',
                            // `?? null`: a missing value is a gap, never a zero.
                            data: (chartData?.connects || []).map((v) => v ?? null),
                            backgroundColor: connectColor,
                            borderColor: connectColor,
                            borderWidth: 3,
                            borderDash: [6, 4],
                            pointRadius: 4,
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 1,
                            pointHoverRadius: 8,
                            pointBackgroundColor: connectColor,
                            pointHoverBackgroundColor: connectColor,
                            pointHoverBorderColor: '#ffffff',
                            pointHoverBorderWidth: 2,
                            tension: 0.4,
                            spanGaps: false,
                            fill: false,
                            yAxisID: 'b',
                            order: 0,
                        }] : []),
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        annotation: {
                            drawTime: 'afterDatasetsDraw',
                            annotations: {
                                line1: {
                                    type: 'line',
                                    borderDash: [8, 4],
                                    borderWidth: 4,
                                    scaleID: 'b',
                                    borderColor: '#BE4D2578',
                                    value: avgResponseTime || 0,
                                    label: {
                                        display: false,
                                        backgroundColor: '#BE4D25',
                                        borderColor: '#000000',
                                        borderWidth: 2,
                                        content: `Response Time Average: ${avgResponseTime || 0}`,
                                    },
                                    enter({ element }) { element.label.options.display = true; return true; },
                                    leave({ element }) { element.label.options.display = false; return true; },
                                },
                            },
                        },
                        legend: { display: true },
                    },
                    scales: {
                        x: {
                            title: { display: true, text: 'Time of the Day' },
                            grid: { display: false },
                        },
                        a: {
                            id: 'a',
                            position: 'left',
                            title: { display: true, text: 'Average Alarms', color: '#0081C7', font: { size: 14, weight: '600' } },
                            grid: { display: false },
                            suggestedMin: 0,
                        },
                        b: {
                            id: 'b',
                            position: 'right',
                            title: { display: true, text: isGrandstream ? 'Avg Response / Connect Time (min)' : 'Average Response Time (min)', color: '#BE4D25', font: { size: 14, weight: '600' } },
                            grid: { display: false },
                            suggestedMax: 1,
                            suggestedMin: 0,
                        },
                    },
                },
            });
        }

        return () => {
            if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
        };
    }, [chartData, avgResponseTime, labels, barColors, isGrandstream, connectColor]);

    return (
        <div className="md-section">
            <div className="md-section__header">
                <h3 className="md-section__title">Average Alarms and Response Times by Hour<HeaderInfoTip title="About This Chart" body={isGrandstream ? `${HOURLY_CHART_TOOLTIP.join(' ')} ${CONNECT_TOOLTIP}` : HOURLY_CHART_TOOLTIP.join(' ')} /></h3>
                {/* Shift legend */}
                {shiftLegend.length > 0 ? (
                    <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
                        {shiftLegend.map((s) => (
                            <ShiftLegendItem key={s.name} name={s.name} color={s.color} hours={s.hours} />
                        ))}
                    </div>
                ) : null}
            </div>
            <div className="md-chart-container">
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
};

export default HourlyChart;
